import {
  Button,
  column,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  ModalBody,
  ModalFooter,
  ModalHeader,
  simpleHeader,
  useModal,
} from "@homebound/beam";
import FileSaver from "file-saver";
import { useRef, useState } from "react";
import { useAlertContext } from "src/components/AlertContext";
import { useController } from "src/hooks/useController";
import {
  UwConfigChange,
  UwConfigChangeSet,
  UwConfigExport,
  UwConfigExportEndpoint,
  UwConfigImportEndpoint,
} from "./UwConfigEndpoints";
import { useAfterUwConfigWrite } from "./useAfterUwConfigWrite";
import { UwConfigTypeDef } from "./uwConfigFields";

type UwConfigImportExportProps = {
  typeDef: UwConfigTypeDef;
};

/** Export the config as JSON, and import an edited export (previewing its changes before applying them) */
export function UwConfigImportExport({ typeDef }: UwConfigImportExportProps) {
  const { fetch } = useController();
  const { showError } = useAlertContext();
  const { openModal } = useModal();
  const inputRef = useRef<HTMLInputElement>(null);

  async function onExport() {
    const exported: UwConfigExport = await fetch(UwConfigExportEndpoint, { configType: typeDef.type });
    const blob = new Blob([JSON.stringify(exported, null, 2)], { type: "application/json" });
    FileSaver.saveAs(blob, `uw-config-${typeDef.type}-v${exported.exported_version}.json`);
  }

  async function onFile(file: File) {
    let document: UwConfigExport;
    try {
      document = JSON.parse(await readText(file));
    } catch {
      return showError(`${file.name} isn't valid JSON`);
    }
    try {
      const preview: UwConfigChangeSet = await fetch(UwConfigImportEndpoint, {
        configType: typeDef.type,
        document,
        dryRun: true,
      });
      openModal({
        content: <UwConfigImportPreview typeDef={typeDef} document={document} preview={preview} />,
        size: "xl",
      });
    } catch {
      // useController showed the validation error from the API
    }
  }

  return (
    <div css={Css.df.gap1.$}>
      <Button variant="secondary" label="Export JSON" icon="download" onClick={onExport} />
      <Button variant="secondary" label="Import JSON" icon="upload" onClick={() => inputRef.current?.click()} />
      <input
        type="file"
        accept=".json,application/json"
        ref={inputRef}
        css={Css.dn.$}
        data-testid="importFile"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          // clear the input so the same file can be picked again after fixing it
          e.target.value = "";
          if (file) await onFile(file);
        }}
      />
    </div>
  );
}

type UwConfigImportPreviewProps = {
  typeDef: UwConfigTypeDef;
  document: UwConfigExport;
  preview: UwConfigChangeSet;
};

export function UwConfigImportPreview({ typeDef, document, preview }: UwConfigImportPreviewProps) {
  const { closeModal } = useModal();
  const { fetch } = useController();
  const afterWrite = useAfterUwConfigWrite();
  const [applying, setApplying] = useState(false);
  const { created, updated, deleted } = preview.summary;
  const count = preview.changes.length;

  async function onApply() {
    setApplying(true);
    try {
      await fetch(UwConfigImportEndpoint, { configType: typeDef.type, document, dryRun: false });
    } catch {
      // e.g. someone else changed the config since it was exported; useController showed the message
      setApplying(false);
      return;
    }
    closeModal();
    await afterWrite(typeDef.type, `Imported ${count} change${count === 1 ? "" : "s"} to ${typeDef.label}`);
  }

  return (
    <>
      <ModalHeader>Import {typeDef.label}</ModalHeader>
      <ModalBody>
        {count === 0 ? (
          <div css={Css.base.$}>This file matches the current config, so there's nothing to import.</div>
        ) : (
          <div css={Css.dg.gap2.$}>
            <div css={Css.base.$} data-testid="importSummary">
              {created} added · {updated} changed · {deleted} deleted
            </div>
            <GridTable columns={changeColumns} rows={changeRows(preview.changes)} style={{ allWhite: true }} />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" label={count === 0 ? "Close" : "Cancel"} onClick={closeModal} />
        {count > 0 && (
          <Button label={`Apply ${count} Change${count === 1 ? "" : "s"}`} onClick={onApply} disabled={applying} />
        )}
      </ModalFooter>
    </>
  );
}

function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

type ChangeRow = { kind: "header" } | { kind: "data"; data: UwConfigChange };

export const changeColumns: GridColumn<ChangeRow>[] = [
  column<ChangeRow>({ header: "Action", data: ({ action }) => actionLabels[action], w: "90px" }),
  column<ChangeRow>({ header: "Row", data: ({ row_label }) => row_label, w: 2 }),
  column<ChangeRow>({ header: "Field", data: ({ field }) => field ?? "", w: 1 }),
  column<ChangeRow>({ header: "Before", data: ({ old_value }) => changeValue(old_value), w: 1 }),
  column<ChangeRow>({ header: "After", data: ({ new_value }) => changeValue(new_value), w: 1 }),
];

export function changeRows(changes: UwConfigChange[]): GridDataRow<ChangeRow>[] {
  return [simpleHeader, ...changes.map((c, i) => ({ kind: "data" as const, id: `${i}`, data: c }))];
}

const actionLabels: Record<UwConfigChange["action"], string> = { create: "Add", update: "Change", delete: "Delete" };

/** Created/deleted rows carry the whole row; show its values compactly */
export function changeValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object" && !Array.isArray(value)) {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== null)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
      .join("; ");
  }
  return Array.isArray(value) ? value.join(", ") : String(value);
}
